/**
 * Execution Ledger — platform-ops monitor for every mission + harness
 * terminal execution across all orgs.
 *
 * Layout mirrors [ActivityBrowser.tsx](./ActivityBrowser.tsx) (handoff
 * `ai-docs/handoffs/execution-ledger-lexportal-monitoring-handoff-2026-06-22.md` §1):
 *   header → ExecutionLedgerSummary → ExecutionLedgerFilters →
 *   master/detail grid (list left, ExecutionLedgerDetailPane right).
 *
 * Selection is URL-bound: `/execution-ledger/:executionId` is the
 * deep link. Filter changes do NOT clear the URL — the detail pane
 * shows "not in current view" when the selection falls outside the
 * filter (same UX as ActivityBrowser).
 *
 * Data path: `usePlatformApi()` → `/api/platform/executions[/:id|/stats]`
 * (NOT `useGatewayRpc` — this is centralized lexserver data, not
 * per-runtime). The SPA is already behind `RequirePlatformAdmin` and
 * the API enforces `requirePlatformAdmin` server-side too.
 *
 * Read-only — no cancel/resume here (that's the per-user LexIDE
 * detail-dialog handoff). Window is bounded to the TTL (90 days by
 * default; older runs are purged).
 *
 * SLICE 2: page shell + sidenav + route wiring + minimal list
 * rendering against the live API. Filters / summary / detail pane
 * land in subsequent slices (placeholders today).
 */

import { Activity, Loader2, Paperclip } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ExecutionLedgerDetailPane } from "@/components/execution-ledger/ExecutionLedgerDetailPane";
import {
  EMPTY_EXECUTION_FILTER,
  ExecutionLedgerFilters,
  toApiFilter,
  type ExecutionLedgerFilterState,
} from "@/components/execution-ledger/ExecutionLedgerFilters";
import { ExecutionLedgerSummary } from "@/components/execution-ledger/ExecutionLedgerSummary";
import { ExecutionLedgerTopOrgs } from "@/components/execution-ledger/ExecutionLedgerTopOrgs";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { PlatformApiError } from "@/lib/platform-api";
import type {
  ExecutionLedgerRecord,
  ExecutionLedgerStatsResult,
} from "@/types/execution-ledger";

const PAGE_SIZE = 50;

export function ExecutionLedger() {
  const api = usePlatformApi();
  const navigate = useNavigate();
  const { executionId: urlExecutionId } = useParams<{ executionId?: string }>();

  const [filter, setFilter] = useState<ExecutionLedgerFilterState>(
    EMPTY_EXECUTION_FILTER,
  );
  const [items, setItems] = useState<ExecutionLedgerRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail-pane state — fetched independently of the list so a deep-link
  // load (the user pastes /execution-ledger/:id) works even when the
  // selection isn't on the current filter page.
  const [detailRecord, setDetailRecord] = useState<ExecutionLedgerRecord | null>(null);
  const [detailLeaves, setDetailLeaves] = useState<ExecutionLedgerRecord[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailNotInView, setDetailNotInView] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Summary stats — fetched on filter change with the same wire-format
  // filter the list uses, plus the bucket toggle the user picks.
  const [stats, setStats] = useState<ExecutionLedgerStatsResult | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [bucket, setBucket] = useState<"hour" | "day">("day");

  const selectedId = urlExecutionId ?? null;
  const setSelectedId = useCallback(
    (id: string | null) => {
      navigate(id ? `/execution-ledger/${id}` : "/execution-ledger");
    },
    [navigate],
  );

  // Serialize filter state to the wire format once per state change; the
  // resulting object is referentially stable across renders that don't
  // change a field, so the load effect doesn't refire spuriously.
  const apiFilter = useMemo(() => toApiFilter(filter), [filter]);

  const load = useCallback(
    async (append: boolean, appendCursor?: string) => {
      try {
        if (!append) setLoading(true);
        const res = await api.listExecutions(apiFilter, {
          limit: PAGE_SIZE,
          cursor: append ? appendCursor : undefined,
        });
        setItems((prev) => (append ? [...prev, ...res.data.records] : res.data.records));
        setCursor(res.data.nextCursor);
        setError(null);
      } catch (e) {
        setError(
          e instanceof PlatformApiError
            ? `${e.code}: ${e.message}`
            : e instanceof Error
              ? e.message
              : "Failed to load executions",
        );
      } finally {
        setLoading(false);
      }
    },
    [api, apiFilter],
  );

  // Reload from cursor=null on any filter change OR on api-ref change
  // (token refresh). Cursor is intentionally NOT a dep — pagination
  // never invalidates the list; it appends. The load-more button
  // passes the current cursor explicitly via `appendCursor`.
  useEffect(() => {
    setCursor(null);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, apiFilter]);

  // Stats fetch — same filter window as the list (apiFilter) plus the
  // bucket toggle. Failures are non-fatal: the summary cards just show
  // "0" until the next attempt. Cancellation guard so a rapid bucket
  // toggle doesn't race.
  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    api
      .getExecutionStats(apiFilter, bucket)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, apiFilter, bucket]);

  // Detail fetch — runs whenever the URL selection changes. The list
  // and detail are decoupled on purpose: a deep-link `/execution-ledger/:id`
  // load works even when the row isn't on the current filter page.
  // A 404 sets `notInView` so the pane shows the friendly message
  // rather than a generic error.
  useEffect(() => {
    if (!selectedId) {
      setDetailRecord(null);
      setDetailLeaves([]);
      setDetailNotInView(false);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailNotInView(false);
    api
      .getExecution(selectedId)
      .then((res) => {
        if (cancelled) return;
        setDetailRecord(res.data.record);
        setDetailLeaves(res.data.leaves ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof PlatformApiError && e.status === 404) {
          setDetailNotInView(true);
          setDetailRecord(null);
          setDetailLeaves([]);
        } else {
          setDetailError(
            e instanceof PlatformApiError
              ? `${e.code}: ${e.message}`
              : e instanceof Error
                ? e.message
                : "Failed to load execution detail",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedId]);

  const hasMore = cursor !== null;

  return (
    <div className="flex h-full flex-col gap-4" data-testid="execution-ledger-page">
      <header className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-indigo-500" />
        <h1 className="text-xl font-semibold">Executions</h1>
        <span className="text-sm text-slate-400 dark:text-slate-500">
          Every mission run + harness execution across all orgs (90-day window)
        </span>
      </header>

      <ExecutionLedgerSummary
        stats={stats}
        loading={statsLoading}
        bucket={bucket}
        onBucketChange={setBucket}
      />

      <ExecutionLedgerFilters value={filter} onChange={setFilter} />

      {error && (
        <div
          className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-400"
          data-testid="execution-ledger-error"
        >
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        <ExecutionList
          items={items}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={() => load(true, cursor ?? undefined)}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selectedId ? (
          <ExecutionLedgerDetailPane
            selectedId={selectedId}
            record={detailRecord}
            leaves={detailLeaves}
            loading={detailLoading}
            notInView={detailNotInView}
            error={detailError}
            onSelect={setSelectedId}
          />
        ) : (
          // No execution selected → repurpose the right column as a
          // discovery surface (top orgs by count). When the operator
          // selects a row, the detail pane takes over.
          <ExecutionLedgerTopOrgs
            topOrgs={stats?.topOrgs ?? []}
            loading={statsLoading}
            activeOrgId={filter.orgId || undefined}
            onOrgClick={(orgId) =>
              setFilter((prev) => ({
                ...prev,
                // Toggle: clicking the active org clears the filter.
                orgId: prev.orgId === orgId ? "" : orgId,
              }))
            }
          />
        )}
      </div>
    </div>
  );
}

// ─── Minimal list (slice 2 — replaced with the real master grid in slice 3) ─

interface ListProps {
  items: ExecutionLedgerRecord[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function ExecutionList({
  items,
  loading,
  hasMore,
  onLoadMore,
  selectedId,
  onSelect,
}: ListProps) {
  return (
    <div className="flex min-h-0 flex-col rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <span className="text-sm font-semibold">
          {items.length} execution{items.length === 1 ? "" : "s"}
        </span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="execution-ledger-list">
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-400">
            No executions in the current window. Forwarders ship terminal
            records only — see plan §J for the no-backfill caveat.
          </div>
        )}
        {items.map((r) => (
          <button
            type="button"
            key={r.executionId}
            onClick={() => onSelect(r.executionId)}
            className={`flex w-full flex-col gap-0.5 border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
              selectedId === r.executionId
                ? "bg-indigo-50 dark:bg-indigo-950"
                : ""
            }`}
            data-testid={`execution-ledger-row-${r.executionId}`}
          >
            <div className="flex items-center gap-2">
              {r.kind === "upload" && (
                <Paperclip className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
              )}
              <span className={kindPillCls(r.kind)}>{r.kind}</span>
              <span className={statusPillCls(r.status)}>{r.status}</span>
              <span className="truncate font-medium">{r.title ?? r.summary}</span>
              <span className="ml-auto text-xs text-slate-400">
                {new Date(r.createdAt).toLocaleString()}
              </span>
            </div>
            {/* When `title` is the headline, also show `summary` as the
                subtitle (per handoff §4 — primary row label is `title`,
                essence below). Older records with no `title` already
                used summary as the headline above; skip the duplicate. */}
            {r.title && r.summary && (
              <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                {r.summary}
              </span>
            )}
          </button>
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="w-full border-t border-slate-200 py-2 text-sm font-medium text-indigo-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-indigo-400 dark:hover:bg-slate-800"
            data-testid="execution-ledger-load-more"
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}

/** Kind pill — distinct color per discriminator. Mission=violet,
 *  harness=sky, upload=amber (matches the Paperclip icon's tone so the
 *  row reads as one upload-themed cluster). */
function kindPillCls(kind: string): string {
  const base =
    "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide";
  if (kind === "mission") {
    return `${base} bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200`;
  }
  if (kind === "upload") {
    return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200`;
  }
  return `${base} bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200`;
}

/**
 * Status pill — terminal statuses only (the ledger never carries
 * running/queued/awaiting rows; see plan §G "forward when terminal").
 * `awaiting_user_timeout` is rendered with the same red as `failed`
 * because the actual `error.code` discriminator is shown in the
 * detail pane (handoff §4 — "timed out waiting for input").
 */
function statusPillCls(status: string): string {
  const base =
    "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide";
  if (status === "completed") {
    return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200`;
  }
  if (status === "canceled") {
    return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  }
  // failed / awaiting_user_timeout / any unknown terminal
  return `${base} bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200`;
}

